// evo/rooms/EvoRoomCancellationOptions.styled.ts
// Dedicated styled components for the cancellation-type radio group shown inside
// EvoCancellationTypeSelectionModal. Self-contained — no dependency on the shared
// RoomPanel* components.
import styled from 'styled-components';
import { Radio, RadioLabel, Text } from '@lmn/design-system';

// Two shared columns: a flexible label column and a price column sized to the widest price across
// all rows. Each Option is a subgrid spanning both columns, so the price column reserves the same
// width on every row — including rows that currently show no price difference. The label column
// therefore keeps a constant width and never reflows when the selected option moves the price
// from one row to another, so changing the selection no longer shifts the layout.
export const OptionList = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  // Figma: no gap between rows — the rows' vertical padding provides the rhythm.
  row-gap: ${({ theme }) => theme.kallisto.space.space0};
  // Figma: 24px (space300) between the label column and the price column. The Option subgrids
  // inherit these gutters, so this is the single place the column gap is set.
  column-gap: ${({ theme }) => theme.kallisto.space.space300};
`;

// Clickable radio row. The whole row handles selection (the radio itself is
// presentational, see OptionRadio), so keyboard/click live here once.
// Subgrid: spans both list columns and adopts their tracks, so the label and price land in the
// shared columns and align across every row.
export const Option = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  // Figma: 12px (space150) vertical padding, flush horizontally. The negative horizontal margin
  // cancels the horizontal padding so the content rests flush with the list edges while the row
  // itself — the click target, and what the focus ring traces — still extends past the text.
  margin: 0 ${({ theme }) => theme.kallisto.space.spaceNegative100};
  padding: ${({ theme }) => `${theme.kallisto.space.space150} ${theme.kallisto.space.space100}`};
  cursor: pointer;

  /* The row owns selection, so it owns the focus ring — but only for keyboard/AT focus
     (:focus-visible), not mouse clicks. Suppress the default outline on plain :focus so a
     mouse click that focuses the row doesn't show the ring. */
  &:focus {
    outline: none;
  }

  /* The shared focusRing paints its 4px band *outside* the row. In the cancellation-options modal the
     list is the first thing in the body, so the first row sits flush against the top of the scroll
     container, which clips anything drawn past its edge — the ring would come out sliced. Buying it
     room with body padding would put a gap above the options that the design does not have, so the
     row draws the same two 2px bands inward instead: they land inside its own 12px vertical padding,
     clear of the text, and no scroll edge can ever cut them. Same widths and colours as focusRing,
     mirrored — dark against the row's edge, white inside it. */
  &:focus-visible {
    /* A negative outline-offset moves the outline's inner edge in and still draws outward from
       there, so -4px is what lands the white band at 2-4px, just inside the dark one. */
    outline: 2px solid white;
    outline-offset: -4px;
    box-shadow: inset 0 0 0 2px #0a0c0d;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Suppress only the (visually hidden) radio input's focus ring to avoid a double
     indicator — narrowly scoped so any future interactive child keeps its own ring. */
  input[type='radio'] {
    &:focus,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
  }
`;

// The design-system radio rendered as a presentational indicator: it keeps the
// brand-correct selected visual but does not capture clicks (the row does).
// Sits in the label column; min-width:0 lets the 1fr track shrink it so the label wraps within
// the column instead of pushing the price out.
export const OptionRadio = styled(Radio)`
  grid-column: 1;
  min-width: 0;
  padding: 0;
  pointer-events: none;

  ${RadioLabel} {
    padding: 0;
    cursor: pointer;
    // Figma: 12px (space150) between the radio indicator and the label — the DS default is 8px.
    margin-left: ${({ theme }) => theme.kallisto.space.space150};
  }

  input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;

    &:focus {
      outline: none;
    }
  }
`;

// "+ £34" (Title5) followed by the per-person "pp" label, baseline aligned. Sits in the price
// column, flush to the right edge.
export const PriceDifference = styled.div`
  grid-column: 2;
  justify-self: end;
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  white-space: nowrap;
`;

// Promotion label shown under a cancellation option when the promotion is rate-specific (present on
// some options but not all). Mirrors the non-EVO room panel's meal-plan promotion label style.
export const PromotionLabel = styled(Text).attrs({ variant: 'BodySmall', color: 'textPrimaryDefault' })``;
